import React, { createContext, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpRight, ChevronRight, MoreHorizontal } from 'lucide-react';
import { EinstLink } from './EinstLink';
import { naechstesZiel } from './fokus';
import { InfoTipp, zoomVon } from '../../components/Erklaerung';

// Bausteine der Seiten im Einstellungen-Pop-up (Gestaltung 08.10.2026, GESTALTUNG.md 2.6 bis 2.21): Zeilen „Bezeichnung
// links, Steuerung rechts“, Wertelisten, eine Kennzahlenleiste, Aufklapper, Werkzeugzeile, Leerzeilen, Linkzeilen,
// Segmente, Schalter, Zeilenmenü und Spalten. Klassen und Maße stehen in einstellungen.css; außerhalb eines Bereich
// rendern alle Bausteine schlicht an Ort und Stelle.

// Die einzigen neuen Wörter dieser Gestaltung (GESTALTUNG.md 2.22).
export const TEXTE = {
  erlaeuterung: 'Erläuterung',
  weitereAktionen: 'Weitere Aktionen',
  navSuche: 'Einstellungen durchsuchen',
  navSuchePlatzhalter: 'Suchen',
  navSucheLeer: 'Keine Einstellung gefunden.',
} as const;

// Bereich.tsx liefert den Kontext: ob eine Komponente im Pop-up steht und wo die Hauptaktion des Seitenkopfs sitzt.
export const BereichKontext = createContext<{ aktionsStelle: HTMLElement | null } | null>(null);
export const useImEinstellungen = (): boolean => useContext(BereichKontext) !== null;

// Hauptaktion einer Seite aus der Tiefe des Baums rechts in den Seitenkopf (Portal); außerhalb an Ort und Stelle.
export function Bereichsaktion({ children }: { children: React.ReactNode }) {
  const kontext = useContext(BereichKontext);
  if (!kontext) return <>{children}</>;
  return kontext.aktionsStelle ? createPortal(children, kontext.aktionsStelle) : null;
}

const vorhanden = (knoten: React.ReactNode) => knoten !== undefined && knoten !== null && knoten !== false && knoten !== '';

// Eine Einstellung: Bezeichnung (mit fuer als label des Felds) und Erklärung links, Steuerung rechts, optional darunter
// ein Teil über die volle Breite (etwa ein aufgeklapptes Formular).
// F-24 (PF-TX-rahmen-2): tipp setzt das Symbol mit Tooltip hinter den Titel, außerhalb des label (sonst schaltete ein Klick auf
// das Symbol den Schalter um); ohne tipp unverändert.
export function Zeile({ titel, erklaerung, fuer, id, children, unter, tipp, tippWort }: { titel: React.ReactNode; erklaerung?: React.ReactNode; fuer?: string;
  id?: string; children?: React.ReactNode; unter?: React.ReactNode; tipp?: React.ReactNode; tippWort?: string }) {
  const eigen = useId();
  const wort = tippWort ?? (typeof titel === 'string' ? titel : '');
  const bezeichnung = fuer ? <label className="einst-zeile-titel" htmlFor={fuer} id={id}>{titel}</label>
    : <span className="einst-zeile-titel" id={id ?? `${eigen}-titel`}>{titel}</span>;
  return <div className="einst-zeile">
    <div className="einst-zeile-text">
      {vorhanden(tipp) && wort ? <div className="erkl-titelzeile">{bezeichnung}<InfoTipp wort={wort} text={tipp} /></div> : bezeichnung}
      {vorhanden(erklaerung) && <p className="einst-zeile-erklaerung">{erklaerung}</p>}
    </div>
    {vorhanden(children) && <div className="einst-zeile-steuerung">{children}</div>}
    {vorhanden(unter) && <div className="einst-zeile-unter">{unter}</div>}
  </div>;
}

export function Zeilen({ children, label }: { children: React.ReactNode; label?: string }) {
  return <div className="einst-zeilen" role={label ? 'group' : undefined} aria-label={label}>{children}</div>;
}

// Werteliste: je Angabe eine Zeile, Bezeichnung links, Wert rechts; zweispaltig ab 640 px Bereichsbreite.
export function Werte({ children, zweispaltig, label }: { children: React.ReactNode; zweispaltig?: boolean; label?: string }) {
  return <dl className={zweispaltig ? 'einst-werte einst-werte--zweispaltig' : 'einst-werte'} aria-label={label}>{children}</dl>;
}

export function Wert({ titel, children, zusatz }: { titel: React.ReactNode; children: React.ReactNode; zusatz?: React.ReactNode }) {
  return <div><dt>{titel}</dt><dd>{children}{vorhanden(zusatz) && <small>{zusatz}</small>}</dd></div>;
}

// Kennzahlen als eine Leiste statt Kacheln.
export function Kennzahlen({ children, label }: { children: React.ReactNode; label?: string }) {
  return <div className="einst-kennzahlen" role="group" aria-label={label}>{children}</div>;
}

export function Kennzahl({ titel, wert, zusatz, symbol }: { titel: React.ReactNode; wert: React.ReactNode; zusatz?: React.ReactNode; symbol?: React.ReactNode }) {
  return <div className="einst-kennzahl">
    <span className="einst-kennzahl-titel">{vorhanden(symbol) && <span className="einst-kennzahl-symbol" aria-hidden="true">{symbol}</span>}{titel}</span>
    <strong className="einst-kennzahl-wert">{wert}</strong>
    {vorhanden(zusatz) && <small>{zusatz}</small>}
  </div>;
}

// Längere Erklärtexte und selten gebrauchte Teile zugeklappt; Vorgabe-Titel „Erläuterung“.
export function Aufklapper({ titel = TEXTE.erlaeuterung, children, offen }: { titel?: React.ReactNode; children: React.ReactNode; offen?: boolean }) {
  return <details className="einst-aufklapper" open={offen}>
    <summary><ChevronRight size={16} aria-hidden="true" /><span>{titel}</span></summary>
    <div className="einst-aufklapper-inhalt">{children}</div>
  </details>;
}

// Suche, Filter und Aktionen über einer Tabelle in einer Zeile.
export function Werkzeugleiste({ children }: { children: React.ReactNode }) {
  return <div className="einst-werkzeuge">{children}</div>;
}

export function Leer({ children }: { children: React.ReactNode }) {
  return <p className="einst-leer">{children}</p>;
}

// Leerzustand einer Tabelle als eine ruhige Zeile in ihrem Körper.
export function LeerZeile({ spalten, children }: { spalten: number; children: React.ReactNode }) {
  return <tr className="einst-leer-zeile"><td colSpan={spalten}>{children}</td></tr>;
}

// Eine ganze Zeile als Weg: to im Pop-up (EinstLink, der Hintergrund bleibt), href als Link (extern in neuem Fenster),
// sonst ein Knopf. Gesperrt wird immer ein gesperrter Knopf, damit kein Weg ohne Wirkung klickbar bleibt.
export function Linkzeile({ to, href, extern, onClick, disabled, children, erklaerung }: { to?: string; href?: string; extern?: boolean;
  onClick?: () => void; disabled?: boolean; children: React.ReactNode; erklaerung?: React.ReactNode }) {
  const inhalt = <>
    <span className="einst-linkzeile-text"><span className="einst-linkzeile-titel">{children}</span>
      {vorhanden(erklaerung) && <span className="einst-linkzeile-erklaerung">{erklaerung}</span>}</span>
    {extern ? <ArrowUpRight size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
  </>;
  if (disabled || (!to && !href)) return <button type="button" className="einst-linkzeile" onClick={onClick} disabled={disabled}>{inhalt}</button>;
  if (to) return <EinstLink className="einst-linkzeile" to={to} onClick={onClick}>{inhalt}</EinstLink>;
  return <a className="einst-linkzeile" href={href} onClick={onClick} {...(extern ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>{inhalt}</a>;
}

// Zwei bis vier Wahlen als Segmentschalter mit echten Optionsfeldern (Tastatur und Bildschirmleser wie bei Radios).
// lesend: bleibt in einer Sicht bedienbar (data-lesend, administration/readOnly.tsx), etwa das Farbschema.
// Schlusslauf 4 (M-4, Ä-19 Punkt 3): beschriftungId zeigt auf eine sichtbare Beschriftung des Aufrufers (aria-labelledby); dann
// entfällt das aria-label, damit die Gruppe einen Namen hat und nicht zwei.
export function Segmente<T extends string>({ name, label, wert, optionen, onWahl, disabled, lesend, beschriftungId }: { name: string; label: string; wert: T;
  optionen: { wert: T; text: string }[]; onWahl: (wert: T) => void; disabled?: boolean; lesend?: boolean; beschriftungId?: string }) {
  return <fieldset className="einst-segmente" role="radiogroup" aria-label={beschriftungId ? undefined : label} aria-labelledby={beschriftungId} disabled={disabled} data-lesend={lesend ? '' : undefined}>
    {optionen.map(option => <label key={option.wert} className="einst-segment">
      <input type="radio" name={name} value={option.wert} checked={wert === option.wert} onChange={() => onWahl(option.wert)} />
      <span>{option.text}</span>
    </label>)}
  </fieldset>;
}

// Schalter für sofort wirkende Einstellungen: ein Kontrollkästchen mit der Rolle switch.
export function Schalter({ className, ...rest }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} type="checkbox" role="switch" className={className ? `einst-schalter ${className}` : 'einst-schalter'} />;
}

// Weitere Aktionen einer Zeile hinter einem Knopf mit drei Punkten. Die Liste schwebt fixiert am Knopf, damit Tabellen
// mit waagrechtem Scrollen sie nicht abschneiden; sie steht im DOM beim Knopf (Fokusfalle und Lesegrenze greifen).
export function Zeilenmenue({ label, children }: { label: string; children: React.ReactNode }) {
  const [offen, setOffen] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const liste = useRef<HTMLDivElement>(null);
  const lage = useRef<DOMRect | null>(null);
  const listeId = useId();
  const schliessen = (zurueck: boolean) => { setOffen(false); if (zurueck) knopf.current?.focus({ preventScroll: true }); };

  // Lage: rechte Kante am Knopf, darunter; nach oben, wenn unten kein Platz ist. Ein Vorfahr kann fixierte Elemente an
  // sich binden (Containerabfrage); deshalb erst bei 0/0 messen und um den Versatz berichtigen.
  useLayoutEffect(() => {
    const k = knopf.current, l = liste.current;
    if (!offen || !k || !l) return;
    l.style.left = '0px'; l.style.top = '0px';
    const basis = l.getBoundingClientRect(), r = k.getBoundingClientRect();
    lage.current = r;
    const z = zoomVon(l), hoehe = l.offsetHeight * z, breite = l.offsetWidth * z;
    const top = r.bottom + 4 + hoehe <= window.innerHeight - 8 ? r.bottom + 4 : Math.max(8, r.top - 4 - hoehe);
    const left = Math.min(Math.max(8, r.right - breite), Math.max(8, window.innerWidth - breite - 8));
    l.style.left = `${Math.round((left - basis.left) / z)}px`; l.style.top = `${Math.round((top - basis.top) / z)}px`;
    // DR-13 UI-29: Menü wie das Chat-Menü (AppSidebar.tsx): die Einträge sind menuitem, der Fokus geht auf den ersten bedienbaren;
    // sind alle gesperrt, auf die Liste selbst, damit Escape und die Ansage des Menüs auch dann greifen.
    for (const e of eintraege(l, true)) e.setAttribute('role', 'menuitem');
    (eintraege(l)[0] ?? l).focus({ preventScroll: true });
  }, [offen]);

  // Schließen bei Klick außerhalb, beim Scrollen (auch des Inhalts) und bei Größenänderung.
  useEffect(() => {
    if (!offen) return;
    const aussen = (event: MouseEvent) => {
      const ziel = event.target as Node;
      if (!liste.current?.contains(ziel) && !knopf.current?.contains(ziel)) setOffen(false);
    };
    const zu = () => setOffen(false);
    // Schlusslauf 3 (M3, Konsole): nur ein Scrollen, das den Knopf bewegt (Seite, Inhalt, Tabelle), schließt die Liste. Ein
    // Suchfeld mit langem Text scrollt beim Verlassen seinen eigenen Inhalt zurück; das schloss die eben geöffnete Liste sofort.
    // Schlusslauf 3 (PF-SL3-geld-1, kontingent-geld): das Ereignis eines Scrollens, das den Knopf unmittelbar vor dem Klick ins
    // Bild gerollt hat, kommt erst nach dem Öffnen an. Deshalb schließt die Liste nur, wenn der Knopf gegenüber seiner Lage beim
    // Öffnen um mehr als 1 px verschoben ist.
    const verschoben = () => {
      const vorher = lage.current, jetzt = knopf.current?.getBoundingClientRect();
      return !vorher || !jetzt || Math.abs(jetzt.top - vorher.top) > 1 || Math.abs(jetzt.left - vorher.left) > 1;
    };
    const gescrollt = (event: Event) => {
      const ziel = event.target;
      if ((!(ziel instanceof Node) || ziel === document || ziel.contains(knopf.current)) && verschoben()) setOffen(false);
    };
    document.addEventListener('mousedown', aussen, true);
    window.addEventListener('scroll', gescrollt, true);
    window.addEventListener('resize', zu);
    return () => {
      document.removeEventListener('mousedown', aussen, true);
      window.removeEventListener('scroll', gescrollt, true);
      window.removeEventListener('resize', zu);
    };
  }, [offen]);

  // Escape schließt und gibt den Fokus an den Knopf zurück; Pfeiltasten gehen durch die bedienbaren Einträge.
  const tasten = (event: React.KeyboardEvent) => {
    if (!offen) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); schliessen(true); return; }
    if ((event.key !== 'ArrowDown' && event.key !== 'ArrowUp') || !liste.current) return;
    const knoepfe = eintraege(liste.current);
    const ziel = naechstesZiel(knoepfe.length, knoepfe.indexOf(document.activeElement as HTMLElement), event.key === 'ArrowUp');
    if (ziel < 0) return;
    event.preventDefault();
    knoepfe[ziel].focus();
  };

  return <div className="einst-zeilenmenue" onKeyDown={tasten}
    onBlur={event => { if (offen && !event.currentTarget.contains(event.relatedTarget as Node | null)) setOffen(false); }}>
    <button ref={knopf} type="button" className="einst-symbolknopf einst-zeilenmenue-knopf" aria-label={label} title={label} aria-expanded={offen}
      aria-haspopup="menu" aria-controls={offen ? listeId : undefined} onClick={() => setOffen(wert => !wert)}><MoreHorizontal size={16} aria-hidden="true" /></button>
    {/* Schlusslauf 3 M2: ein Eintrag gibt den Fokus an den Knopf zurück, bevor sein Dialog aufgeht; der Dialog merkt sich den Knopf
        als Rücksprungziel (components.tsx Dialog) statt des entfernten Eintrags, und nach Escape oder „Abbrechen“ steht der Fokus wieder auf „…“. */}
    {offen && <div ref={liste} id={listeId} className="einst-zeilenmenue-liste" role="menu" aria-label={label} tabIndex={-1}
      onClick={event => { if ((event.target as HTMLElement).closest('button, a')) schliessen(true); }}>{children}</div>}
  </div>;
}

/** Die Einträge eines Zeilenmenüs: Knöpfe und Links direkt in der Liste (auch aus Fragmenten); ohne alle nur die bedienbaren. */
const eintraege = (liste: HTMLElement, alle = false) => Array.from(liste.querySelectorAll<HTMLElement>(':scope > button, :scope > a[href]'))
  .filter(e => alle || !(e as HTMLButtonElement).disabled);

// Zwei bis vier kurze Abschnitte nebeneinander (ab 760 px Bereichsbreite zwei Spalten).
export function Spalten({ children }: { children: React.ReactNode }) {
  return <div className="einst-spalten">{children}</div>;
}
